import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MEGA_MENU } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/contact", label: "Contact" },
];

const MegaPanel = ({ menu, onClose }) => (
  <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 ${menu.width}`}>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="bg-white rounded-3xl border border-slate-100 shadow-[0_24px_60px_rgba(1,25,61,0.14)] p-7 flex gap-7"
    >
      <div className={`grid gap-7 flex-1 ${menu.columns.length > 1 ? "grid-cols-3" : "grid-cols-1"}`}>
        {menu.columns.map((col) => (
          <div key={col.heading}>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#2563EB] mb-3 px-3">{col.heading}</p>
            <div className="space-y-1">
              {col.items.map((item) => (
                <Link
                  key={item.label}
                  to={item.to || menu.to}
                  onClick={onClose}
                  data-testid={`mega-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="block rounded-xl px-3 py-2.5 hover:bg-[#CAF2F9]/50 transition-colors group/item"
                >
                  <p className="text-sm font-semibold text-[#01193D] group-hover/item:text-[#2563EB] transition-colors">{item.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Link
        to="/contact"
        onClick={onClose}
        data-testid="mega-menu-promo-card"
        className="w-[220px] shrink-0 rounded-2xl bg-[#01193D] p-5 flex flex-col justify-between relative overflow-hidden group/promo"
      >
        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#2563EB]/30 blur-2xl" />
        <div className="relative">
          <p className="font-display font-semibold text-white text-sm leading-snug">{menu.promo.title}</p>
          <p className="text-xs text-slate-300 mt-2 leading-relaxed">{menu.promo.desc}</p>
        </div>
        <span className="relative inline-flex items-center gap-1.5 text-xs font-semibold text-[#09B2ED] mt-4 group-hover/promo:gap-2.5 transition-all">
          {menu.promo.cta} <ArrowUpRight size={13} />
        </span>
      </Link>
    </motion.div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();
  const location = useLocation();

  const isDarkPage = location.pathname === "/products/lms";
  const isDark = isDarkPage;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.div
        data-testid="scroll-progress-bar"
        style={{ scaleX: scrollYProgress }}
        className={`fixed top-0 left-0 right-0 h-[2px] origin-left z-[70] ${isDark ? "bg-[#09B2ED]" : "bg-[#2563EB]"}`}
      />
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
          scrolled
            ? isDark
              ? "bg-[#01193D]/95 backdrop-blur-md border-b border-white/10 shadow-[0_4px_25px_rgba(0,0,0,0.3)]"
              : "bg-white/92 backdrop-blur-md border-b border-slate-100 shadow-[0_4px_20px_rgba(1,25,61,0.03)]"
            : "bg-transparent"
        }`}
      >
        <nav className={`max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-[76px]"}`}>
          <Logo light={isDark} />
          <div className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => {
              const key = l.label.toLowerCase();
              const menu = MEGA_MENU[key];
              if (!menu) {
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-link-${key}`}
                    className={({ isActive }) =>
                      `nav-link ${isDark ? "nav-link-dark" : ""} text-sm font-medium transition-colors ${
                        isDark
                          ? isActive
                            ? "active text-[#09B2ED] font-semibold"
                            : "text-white/90 hover:text-white"
                          : isActive
                          ? "active text-[#2563EB]"
                          : "text-[#01193D] hover:text-[#2563EB]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                );
              }
              return (
                <div
                  key={l.to}
                  className="relative"
                  onMouseEnter={() => setMega(key)}
                  onMouseLeave={() => setMega(null)}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${key}`}
                    className={({ isActive }) =>
                      `nav-link ${isDark ? "nav-link-dark" : ""} inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                        isDark
                          ? isActive
                            ? "active text-[#09B2ED] font-semibold"
                            : "text-white/90 hover:text-white"
                          : isActive
                          ? "active text-[#2563EB]"
                          : "text-[#01193D] hover:text-[#2563EB]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        isDark ? "text-white/80" : ""
                      } ${mega === key ? "rotate-180" : ""}`}
                    />
                  </NavLink>
                  <AnimatePresence>
                    {mega === key && <MegaPanel menu={menu} onClose={() => setMega(null)} />}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="btn-gradient inline-flex items-center gap-2 text-white text-xs font-semibold px-5 py-2.5 rounded-full shadow-md shadow-blue-500/20"
            >
              Start Project <ArrowRight size={13} />
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2.5 rounded-xl transition-colors ${
              isDark
                ? "text-white hover:bg-white/10"
                : "text-[#01193D] hover:bg-[#CAF2F9]"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-white pt-24 px-8 lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-4 text-2xl font-display font-semibold border-b border-slate-100 ${
                        isActive ? "text-[#2563EB]" : "text-[#01193D]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                data-testid="mobile-cta-button"
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="btn-gradient mt-6 text-white font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2"
              >
                Start a Project <ArrowRight size={18} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
